import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import { get, orderBy, startCase, keyBy, isNil, map, isEmpty } from 'lodash';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import {
  MessageBar,
  MessageBarType,
  DefaultButton,
  PrimaryButton,
  DetailsList,
  SelectionMode,
  DetailsRow,
} from 'office-ui-fabric-react';
import { useParams, useHistory } from 'react-router-dom';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import {
  fetchContractActivity,
  discardContractDraft,
  submitForApproval,
  useSelectContractByAddress,
} from '../../actions/contracts';
import { fetchAttachment } from '../../actions/attachments';
import { can, getUser } from '../../utils/auth';
import { formatCurrency, formatDateString, pxToRem } from '../../utils';
import StackGrid from '../../components/StackGrid';
import FluentGrid from '../../components/FluentGrid';
import CardHeader from '../../components/CardHeader';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModule from '../../components/SubModule';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader/ModuleHeader';
import ModuleContent from '../../components/ModuleContent';
import SubModuleContent from '../../components/SubModuleContent';
import DataList from '../../components/DataList';
import DataItem from '../../components/DataItem';
import SectionHeader from '../../components/SectionHeader';
import Pill from '../../components/Pill';
import Link from '../../components/Link';
import ContractActivity from '../../components/ContractActivity';
import DocumentsList from '../../components/DocumentsList';
import { statementFrequency } from '../../constants';
import styles from './ContractDetails.style';
import Protected from '../../components/Protected';
import TextButton from '../../components/TextButton';
import FilterDetails, {
  calculationFeaturePrefix,
  calculationPrefix,
  getPrefixSentence,
} from '../../components/FilterDetails';
import useAPI from '../../hooks/useAPI';
import FilterDetailsList from '../../components/FilterDetailsList';
import Dropdown from '../../components/Dropdown';
import TextField from '../../components/TextField';
import { fetchUsersByRoles, useUserSelectorByRoles } from '../../actions/users';
import DiscardContractDialog from '../../components/DiscardContractDialog';
import ContractApprovalOptions from '../../components/ContractApprovalOptions';
import ContractDetailsMessageBar from '../../components/ContractDetailsMessageBar';
import PartnerContractProductList from '../../components/PartnerContractProductList';
import { calcFeatureTypes } from '../../components/CalculationFeatures/constants';
import {
  exportFeatureToExcel,
  exportFeeToExcel,
  exportFilterToExcel,
  getAgreementAndCalcParamHeader,
  getCalculationFeatureHeader,
} from '../../components/FilterDetailsList/FilterDetailsList';
import AdvanceGroupCard from '../../components/AdvanceGroupCard';
import Callout from '../../components/Callout';
import { expandRateAssignments } from '../../components/RoyaltyFeeForm/RoyaltyFeeForm';

const ContractReviewersRoles = ['businessProcessingManager'];

const ContractDetails = () => {
  const dispatch = useDispatch();
  const { contractAddress } = useParams();
  const history = useHistory();
  const [isDiscardHidden, setIsDiscardHidden] = useState(true);
  const [isContractSubmitting, setIsContractSubmitting] = useState(false);

  const contract = useSelectContractByAddress(contractAddress);
  const {
    agreements,
    calculationFeatures,
    advanceGroups,
    attachments: contractAttachments,
  } = contract;
  const { attachments } = contract.metadata;
  const attachmentIds = contractAttachments?.length ? map(contractAttachments, 'attachmentId') : [];

  const activities = useSelector(state =>
    orderBy(
      Object.values(get(state, `entities.activity`, {})).filter(
        activity =>
          activity.params.contractAddress === contractAddress ||
          activity.resource === contractAddress ||
          (activity.area === 'contractattachments' && attachmentIds.includes(activity.resource)),
      ),
      ['timestamp'],
      ['desc'],
    ),
  );

  const reviewers = useAPI(() => fetchUsersByRoles(ContractReviewersRoles), null, {
    disabled: !can('viewUsers'),
    fetchOnMount: true,
    selector: useUserSelectorByRoles(ContractReviewersRoles).map(user => ({
      key: user.oid,
      text: user.displayName,
    })),
  });

  let contractReviewer =
    reviewers && reviewers.data && reviewers.data.length > 0 && contract?.metadata?.reviewerId
      ? reviewers.data.find(reviewer => reviewer.key === contract.metadata.reviewerId)?.text
      : null;

  if (!contractReviewer) {
    contractReviewer = contract?.metadata?.reviewerId;
  }

  const statementFrequencies = keyBy(statementFrequency, 'key');
  // if the contract is in Draft or PendingInitialApproval status, that means it's
  // currently being onboarded for the first time and has not been live yet.
  // if a contract or any agreement is pending approval, this will be true
  const isPendingApproval = ['PendingApproval', 'PendingInitialApproval'].includes(
    contract.approvalStatus,
  );
  const user = getUser();
  const isPartner = get(user, 'profile.roles', []).includes('partner');
  const isDraft = contract.approvalStatus === 'Draft' || contract.approvalStatus === 'Paused';
  const isReviewer = user?.profile.oid === contract?.metadata?.reviewerId;
  const isOnboarding =
    contract.approvalStatus === 'PendingInitialApproval' || (isDraft && !contract.previousState);
  const transactionsPaused =
    ['Draft', 'Paused', 'PendingInitialApproval', 'PendingApproval'].includes(
      contract.approvalStatus,
    ) && !isOnboarding;
  const isEditedPendingApproval = contract.approvalStatus === 'PendingApproval';
  const showReviewerSection =
    ['Draft', 'Paused', 'PendingInitialApproval', 'PendingApproval'].includes(
      contract.approvalStatus,
    ) &&
    (can('submitContractForApproval') || can('discardContractDraft'));

  const submitter =
    (contract.submittedForApprovalBy && contract.submittedForApprovalBy.name) ||
    contract.submittedForApprovalBy;
  const lastUpdatedBy =
    (contract.lastUpdatedBy && contract.lastUpdatedBy.name) || contract.lastUpdatedBy;

  const productsAssociated = contract?.scopeFilter?.filters
    ?.filter(
      item =>
        item?.type === 'include' &&
        new Date(item.effectiveDate).getTime() < new Date().getTime() &&
        new Date().getTime() < new Date(item.terminationDate).getTime(),
    )
    ?.flatMap(dimension => dimension.dimensions);

  useEffect(() => {
    // little hack to allow services time to process the activity log item
    setTimeout(() => {
      dispatch(fetchContractActivity(contractAddress));
    }, 1500);
  }, [dispatch, contractAddress]);

  const handleDownload = (item, open) => {
    dispatch(fetchAttachment(contractAddress, item, open));
  };

  const handleCancel = () => {
    history.push(`/contracts`);
  };

  const discardDraftApi = useAPI(
    () => discardContractDraft(contractAddress, !!contract.previousState),
    [contractAddress],
    {
      fetchOnMount: false,
      autoFetch: false,
      onSuccessCallback: () => {
        if (!contract.previousState) {
          history.push(`/contracts`); // If it is an onboarded contract, go to list page. Else stay here, the view will be reloaded with previousState
        }
      },
    },
  );

  const handleDiscardDraft = () => {
    discardDraftApi.fetchApi();
  };

  const agreementWithRateAssignmentsPresent =
    agreements &&
    agreements.length > 0 &&
    agreements.filter(agreement => agreement.rateAssignments).length > 0;

  const calcFeaturePresent = calculationFeatures && calculationFeatures.length > 0;

  const initialFormValues = {
    reviewerId:
      get(contract, 'metadata.reviewerId') ||
      get(contract, 'metadata.businessProcessingManager.oid'),
    reviewerJustification: get(contract, 'metadata.reviewerJustification'),
  };

  const onSubmitForApproval = async formValues => {
    setIsContractSubmitting(true);
    await dispatch(submitForApproval(contract.address, formValues));
    setIsContractSubmitting(false);
    return {}; // Not doing this will have isSubmitting in Dropdown Field to be true, resulting in dropdown being disabled post submission
  };

  const validateFormValues = formValues => {
    const errors = {};

    if (!formValues.reviewerId) {
      errors.reviewerId = 'Required';
    }

    if (
      !formValues.reviewerJustification &&
      formValues.reviewerId !== get(contract, 'metadata.businessProcessingManager.oid')
    ) {
      errors.reviewerJustification = 'Required';
    }

    return errors;
  };

  const renderCurrency = (currencyType, currencyValue) => {
    const calloutStyles = {
      root: {
        marginLeft: pxToRem(-32),
        selectors: {
          '& .ms-Button': {
            paddingBottom: pxToRem(4),
          },
          '& .ms-Button-icon': {
            fontSize: pxToRem(14),
          },
        },
      },
    };

    let showCallout;
    if (isPendingApproval || isDraft) {
      showCallout = !isPartner && contract.pendingUpdates && contract.pendingUpdates[currencyType];
    } else {
      showCallout = !isEmpty(contract.pendingUpdates);
    }

    return (
      <div className={styles.calloutDisplayContainer}>
        {showCallout && (
          <Callout
            content={
              <>
                {startCase(currencyType)} will change to{' '}
                <strong>{contract.pendingUpdates[currencyType]}</strong> on{' '}
                <strong>
                  {formatDateString(contract.pendingUpdates[`${currencyType}EffectiveDate`])}
                </strong>
              </>
            }
            styleOverride={calloutStyles}
          />
        )}
        <DataItem label={`${startCase(currencyType)}`}>{currencyValue}</DataItem>
      </div>
    );
  };

  const contractDetailsCard = () => {
    return (
      <ModuleContent>
        <CardHeader
          variant="large"
          extra={
            <StackGrid
              smCols={1}
              mdCols={2}
              lgCols={3}
              gap={0}
              shrink
              parentClassName={styles.contractCard}
            >
              {contract.silo && (
                <DataItem label="Contract Silo">{contract.silo.toUpperCase()}</DataItem>
              )}
              {contract.effectiveDate && (
                <Protected permissions="viewContractDates">
                  <DataItem label="Activation Date">
                    {formatDateString(contract.effectiveDate)}
                  </DataItem>
                </Protected>
              )}
              {contract.terminationDate && (
                <Protected permissions="viewContractDates">
                  <DataItem label="Expiration Date">
                    {formatDateString(contract.terminationDate)}
                  </DataItem>
                </Protected>
              )}
              {contract.perCapPercentage && (
                <DataItem label="Offset Cap Rate">{`${contract.perCapPercentage}%`}</DataItem>
              )}
              {contract.contractCurrency &&
                renderCurrency('contractCurrency', contract.contractCurrency)}
              {contract.metadata.statementFrequency && (
                <DataItem label="Statement Frequency">
                  {get(
                    statementFrequencies,
                    `${contract.metadata.statementFrequency}.text`,
                    startCase(contract.metadata.statementFrequency),
                  )}
                </DataItem>
              )}
              {contract.metadata.auditWindow && (
                <Protected permissions="viewAuditWindow">
                  <DataItem label="Audit Window">{contract.metadata.auditWindow} Days</DataItem>
                </Protected>
              )}
            </StackGrid>
          }
          leftBorder
        >
          {`ID: #${contract.contractId || ''}`}
        </CardHeader>

        <SubModule closed className={styles.moduleBottom}>
          <SubModuleHeader id="accountingPaymentHeading">
            Accounting and Payment Details
          </SubModuleHeader>

          <SubModuleContent className={styles.moduleContent}>
            <DataList>
              {contract.metadata.paymentStartDate && (
                <Protected permissions="viewPaymentStartDate">
                  <DataItem label="Payment Start Date">
                    {formatDateString(contract.metadata.paymentStartDate)}
                  </DataItem>
                </Protected>
              )}
              {contract.metadata.paymentCurrency &&
                renderCurrency('paymentCurrency', contract.metadata.paymentCurrency)}
              {!isNil(contract.metadata.paymentTerms) && (
                <DataItem label="Payment Terms">{contract.metadata.paymentTerms} Days</DataItem>
              )}
              <Protected permissions="viewPaymentMinimum">
                <DataItem label="Payment Minimum">
                  {contract.metadata.paymentMinimum
                    ? formatCurrency(
                        contract.metadata.paymentMinimum,
                        contract.metadata.paymentCurrency,
                      )
                    : 'None'}
                </DataItem>
              </Protected>
              {contract.metadata.purchaseClassificationCode && (
                <Protected permissions="viewPurchaseClassificationCode">
                  <DataItem label="Purchase Classification Code">
                    {contract.metadata.purchaseClassificationCode}
                  </DataItem>
                </Protected>
              )}
              {contract.metadata.companyCode && (
                <Protected permissions="viewCompanyCode">
                  <DataItem label="Company Code">{contract.metadata.companyCode}</DataItem>
                </Protected>
              )}
              {contract.metadata.taxCode && (
                <Protected permissions="viewTaxCode">
                  <DataItem label="Tax Code">{contract.metadata.taxCode}</DataItem>
                </Protected>
              )}
              {contract.metadata.costCenter && (
                <DataItem label="Cost Center/Internal Order">
                  {contract.metadata.costCenter}
                </DataItem>
              )}
              {contract.metadata.expenseAccount && (
                <DataItem label="Expense Account">{contract.metadata.expenseAccount}</DataItem>
              )}
              {contract.metadata.liabilityAccount && (
                <DataItem label="Liability Account">{contract.metadata.liabilityAccount}</DataItem>
              )}
              {contract.metadata.productFamily && (
                <DataItem label="Product Family">{contract.metadata.productFamily}</DataItem>
              )}
              {contract.metadata.vendorId && (
                <DataItem label="Vendor Id">{contract.metadata.vendorId}</DataItem>
              )}
            </DataList>
          </SubModuleContent>
        </SubModule>

        {(contract.metadata.keywords ||
          contract.metadata.comment ||
          (attachments && attachments.length > 0)) && (
          <Protected permissions="viewAdditionalContractInfo">
            <SubModule
              closed
              data-testid="additional-information-trigger"
              className={styles.moduleBottom}
            >
              <SubModuleHeader id="additionInfoHeading">Additional Information</SubModuleHeader>

              <SubModuleContent
                data-testid="additional-information-content"
                className={styles.moduleContent}
              >
                <FluentGrid>
                  <FluentGrid row>
                    {contract.metadata.comment && (
                      <FluentGrid col sm={12} md={6}>
                        <SectionHeader noBorder noSpacing>
                          Comments
                        </SectionHeader>
                        <Text as="p" variant="small" className={styles.wordWrapBreakWord}>
                          {contract.metadata.comment}
                        </Text>
                      </FluentGrid>
                    )}

                    {contract.metadata.keywords && (
                      <FluentGrid col sm={12} md={6} className={styles.keywords}>
                        <SectionHeader noBorder noSpacing>
                          Keywords
                        </SectionHeader>

                        <Stack horizontal wrap tokens={{ childrenGap: 8 }}>
                          {contract.metadata.keywords.split(',').map(keyword => (
                            <Pill key={keyword}>{`#${keyword.trim()}`}</Pill>
                          ))}
                        </Stack>
                      </FluentGrid>
                    )}
                  </FluentGrid>
                </FluentGrid>
                {can('viewAttachment') && attachments && attachments.length > 0 && (
                  <div className={styles.attachments}>
                    <SectionHeader>Attachments</SectionHeader>
                    <DocumentsList
                      items={attachments}
                      actionButtons={['download', 'view']}
                      isHeaderVisible={false}
                      downloadDocument={handleDownload}
                    />
                  </div>
                )}
              </SubModuleContent>
            </SubModule>
          </Protected>
        )}

        <Protected permissions="viewContractAssignments">
          <SubModule
            closed
            data-testid="contract-assignments-trigger"
            className={styles.moduleBottom}
          >
            <SubModuleHeader id="contractAssignmentHeading">Contract Assignments</SubModuleHeader>
            <SubModuleContent className={styles.moduleContent}>
              <DataList allowCeil>
                {contract.metadata?.businessAccountManager && (
                  <DataItem label="Business Account Manager">
                    <span className={styles.managerInfoBlock}>
                      {contract.metadata.businessAccountManager.displayName}
                    </span>
                  </DataItem>
                )}
                {contract.metadata?.royaltyOperationsManager && (
                  <DataItem label="Royalty Operations Manager">
                    <span className={styles.managerInfoBlock}>
                      {contract.metadata.royaltyOperationsManager.displayName}
                    </span>
                  </DataItem>
                )}
                {contract.metadata?.contractProcessingManager && (
                  <DataItem label="Contract Processing Manager">
                    <span className={styles.managerInfoBlock}>
                      {contract.metadata.contractProcessingManager.displayName}
                    </span>
                  </DataItem>
                )}
                {contract.metadata?.businessProcessingManager && (
                  <DataItem label="Business Processing Manager">
                    <span className={styles.managerInfoBlock}>
                      {contract.metadata.businessProcessingManager.displayName}
                    </span>
                  </DataItem>
                )}
                {contract.metadata?.royaltyAnalyst && (
                  <DataItem label="Royalty Analyst">
                    <span className={styles.managerInfoBlock}>
                      {contract.metadata.royaltyAnalyst.displayName}
                    </span>
                  </DataItem>
                )}
              </DataList>
            </SubModuleContent>
          </SubModule>
        </Protected>
      </ModuleContent>
    );
  };

  return (
    <>
      {isPartner ? (
        <>
          <Module>
            <ModuleHeader>Contract Terms</ModuleHeader>
            {contractDetailsCard()}
          </Module>
          {/* Associated products */}
          <PartnerContractProductList
            contractName={contract?.name}
            productsAssociated={productsAssociated}
          />
          {/* Calculation Features */}
          <Module>
            <ModuleHeader>Calculation Features</ModuleHeader>
            {calcFeaturePresent ? (
              <>
                {calculationFeatures?.map(feature => (
                  <SubModule>
                    <SubModuleHeader id={`calculationFeaturesHeading${feature.address}`}>
                      {calcFeatureTypes[feature.featureType].text}
                    </SubModuleHeader>
                    <SubModuleContent>
                      <FilterDetails
                        filters={feature.calculations}
                        prefix={calculationFeaturePrefix(feature.calculationMethod)}
                      />
                      <FilterDetailsList
                        filters={feature.calculations}
                        dimensions={feature.dimensions}
                        calcParams={feature.calcParams}
                        onRenderHeader={getCalculationFeatureHeader(feature)}
                        onExport={exportFeatureToExcel(feature)}
                        header="Calculation Feature Details"
                        buttonText="View Feature Details"
                      />
                    </SubModuleContent>
                  </SubModule>
                ))}
              </>
            ) : (
              <ModuleContent>
                <SubModule closed>
                  <SubModuleHeader id="calculationFeaturesHeading">
                    No Calculation Features
                  </SubModuleHeader>
                  <SubModuleContent>
                    <MessageBar>
                      <div>
                        <Text as="p" className={styles.messageBarTitle}>
                          No Calculation Features
                        </Text>
                      </div>
                      <Text as="p" block className={styles.messageBarBody}>
                        No calculation features have been added to this contract.
                      </Text>
                    </MessageBar>
                  </SubModuleContent>
                </SubModule>
              </ModuleContent>
            )}
          </Module>
          {/* Royalty Fee */}
          <Module>
            {/* Only show edit links if the contract is not pending approval */}
            <ModuleHeader>Royalty Fees</ModuleHeader>
            <ModuleContent>
              {!agreementWithRateAssignmentsPresent && (
                <SubModule closed>
                  <SubModuleHeader id="scopeFiltersHeading">No Royalty Fees</SubModuleHeader>
                  <SubModuleContent>
                    <MessageBar>
                      <div>
                        <Text as="p" className={styles.messageBarTitle}>
                          No Royalty Fees Assigned
                        </Text>
                      </div>
                      <Text as="p" block className={styles.messageBarBody}>
                        No royalty fees currently assigned to this contract. Select the Edit Royalty
                        Fees button above to create a new fee.
                      </Text>
                    </MessageBar>
                  </SubModuleContent>
                </SubModule>
              )}

              {agreements?.length > 0 &&
                agreements.map(
                  agreement =>
                    agreement.calculation &&
                    agreement.rateAssignments && (
                      <SubModule key={agreement.address} className={styles.moduleBottom}>
                        <SubModuleHeader id={`royaltyFeesHeading${agreement.address}`}>
                          {agreement.name}
                        </SubModuleHeader>
                        <SubModuleContent className={styles.moduleContent}>
                          <Text as="p" variant="small" block className={styles.calculation}>
                            Calculation Method: <strong>{agreement.calculation.name}</strong>
                          </Text>
                          <FilterDetails
                            filters={agreement.rateAssignments}
                            prefix={calculationPrefix(agreement.calculation)}
                          />
                          <FilterDetailsList
                            dimensions={agreement.dimensions}
                            filters={agreement.rateAssignments}
                            calcParams={agreement.calculation?.calcParams}
                            agreementName={agreement.name}
                            calcMethod={agreement.calculation.name}
                            onRenderHeader={getAgreementAndCalcParamHeader(agreement)}
                            onExport={exportFeeToExcel(agreement)}
                            header="Royalty Fee Details"
                            buttonText="View Fee Details"
                          />
                        </SubModuleContent>
                      </SubModule>
                    ),
                )}
            </ModuleContent>
          </Module>
        </>
      ) : (
        <>
          {/* Show notificiation to user that transactions are paused when a live contract
        or it's associated agreements has pending edits */}
          {transactionsPaused && (
            <MessageBar
              isMultiline={false}
              messageBarType={MessageBarType.error}
              messageBarIconProps={{ iconName: 'Info' }}
              className={styles.transactionsPaused}
            >
              Transactions Paused:{' '}
              <span className={styles.pendingApprovalText}>
                This contract is awaiting approval of calculation critical attributes.
              </span>
            </MessageBar>
          )}

          <div className={styles.actionRequiredWrapper}>
            <ContractDetailsMessageBar
              contractStatus={contract.approvalStatus}
              isReviewer={isReviewer}
              reviewJustification={contract?.metadata?.reviewerJustification}
              reviewer={contractReviewer}
              submitter={submitter}
              lastUpdatedBy={lastUpdatedBy}
              hasContractCreatePermission={can('createContract')}
              isOnboardedContract={!contract.previousState}
              latestActivity={activities.length > 1 ? activities[1] : null}
            />
          </div>

          <Module>
            {/* Only show edit links if the contract is not pending approval */}
            <ModuleHeader
              extra={
                !isPendingApproval && (
                  <Protected permissions="editContract">
                    <Link href={`/contracts/${contractAddress}/add-contract`}>
                      Edit Contract Terms
                    </Link>
                  </Protected>
                )
              }
            >
              Contract Terms
            </ModuleHeader>

            {contractDetailsCard()}
          </Module>

          {/* Scope Filters */}
          <Module>
            {/* Only show edit links if the contract is not pending approval */}
            <ModuleHeader
              extra={
                !isPendingApproval && (
                  <Protected permissions="editContract">
                    <Link href={`/contracts/${contractAddress}/add-contract/scope-filters`}>
                      Edit Scope Filters
                    </Link>
                  </Protected>
                )
              }
            >
              Scope Filters
            </ModuleHeader>
            <ModuleContent>
              <SubModule>
                <SubModuleHeader id="scopeFiltersHeading">Applied Filters</SubModuleHeader>
                <SubModuleContent>
                  {contract.scopeFilter?.filters && contract.scopeFilter?.filters.length > 0 ? (
                    <>
                      <FilterDetails filters={contract.scopeFilter?.filters} />
                      <FilterDetailsList
                        dimensions={contract.scopeFilter?.dimensions}
                        filters={contract.scopeFilter?.filters}
                        header="Scope Filter Details"
                        onExport={exportFilterToExcel(contract.scopeFilter)}
                        buttonText="View Filter Details"
                      />
                    </>
                  ) : (
                    <MessageBar>
                      <div>
                        <Text as="p" className={styles.messageBarTitle}>
                          No Applied Filters
                        </Text>
                      </div>
                      <Text as="p" block className={styles.messageBarBody}>
                        No filters currently added to this contract. Select the Edit Scope Filters
                        button above to create a new filter.
                      </Text>
                    </MessageBar>
                  )}
                </SubModuleContent>
              </SubModule>
            </ModuleContent>
          </Module>

          {/* Calculation Features */}
          <Module>
            <ModuleHeader
              extra={
                !isPendingApproval && (
                  <Protected permissions="editContract">
                    <Link href={`/contracts/${contractAddress}/edit-calc-features`}>
                      Edit Calculation Features
                    </Link>
                  </Protected>
                )
              }
            >
              Calculation Features
            </ModuleHeader>
            {calcFeaturePresent ? (
              <>
                {calculationFeatures?.map(feature => (
                  <SubModule>
                    <SubModuleHeader id={`calculationFeaturesHeading${feature.address}`}>
                      {calcFeatureTypes[feature.featureType].text}
                    </SubModuleHeader>
                    <SubModuleContent>
                      <FilterDetails
                        filters={feature.calculations}
                        prefix={calculationFeaturePrefix(feature.calculationMethod)}
                      />
                      <FilterDetailsList
                        filters={feature.calculations}
                        dimensions={feature.dimensions}
                        calcParams={feature.calcParams}
                        onRenderHeader={getCalculationFeatureHeader(feature)}
                        onExport={exportFeatureToExcel(feature)}
                        header="Calculation Feature Details"
                        buttonText="View Feature Details"
                      />
                    </SubModuleContent>
                  </SubModule>
                ))}
              </>
            ) : (
              <ModuleContent>
                <SubModule closed>
                  <SubModuleHeader id="calculationFeaturesHeading">
                    No Calculation Features
                  </SubModuleHeader>
                  <SubModuleContent>
                    <MessageBar>
                      <div>
                        <Text as="p" className={styles.messageBarTitle}>
                          No Calculation Features
                        </Text>
                      </div>
                      <Text as="p" block className={styles.messageBarBody}>
                        No calculation features have been added to this contract.
                      </Text>
                    </MessageBar>
                  </SubModuleContent>
                </SubModule>
              </ModuleContent>
            )}
          </Module>

          {/* Royalty Fee */}
          <Module>
            {/* Only show edit links if the contract is not pending approval */}
            <ModuleHeader
              extra={
                !isPendingApproval && (
                  <Protected permissions="editContract">
                    <Link href={`/contracts/${contractAddress}/add-royalty-fee`}>
                      Edit Royalty Fees
                    </Link>
                  </Protected>
                )
              }
            >
              Royalty Fees
            </ModuleHeader>
            <ModuleContent>
              {!agreementWithRateAssignmentsPresent && (
                <SubModule closed>
                  <SubModuleHeader id="scopeFiltersHeading">No Royalty Fees</SubModuleHeader>
                  <SubModuleContent>
                    <MessageBar>
                      <div>
                        <Text as="p" className={styles.messageBarTitle}>
                          No Royalty Fees Assigned
                        </Text>
                      </div>
                      <Text as="p" block className={styles.messageBarBody}>
                        No royalty fees currently assigned to this contract. Select the Edit Royalty
                        Fees button above to create a new fee.
                      </Text>
                    </MessageBar>
                  </SubModuleContent>
                </SubModule>
              )}

              {agreements?.length > 0 &&
                agreements.map(
                  agreement =>
                    agreement.calculation &&
                    agreement.rateAssignments && (
                      <SubModule key={agreement.address} className={styles.moduleBottom}>
                        <SubModuleHeader id={`royaltyFeesHeading${agreement.address}`}>
                          {agreement.name}
                        </SubModuleHeader>
                        <SubModuleContent className={styles.moduleContent}>
                          <Text as="p" variant="small" block className={styles.calculation}>
                            Calculation Method: <strong>{agreement.calculation.name}</strong>
                          </Text>
                          {agreement.tieringMethod && (
                            <>
                              <Text as="p" variant="small" block className={styles.calculation}>
                                Tiering Method:{' '}
                                <strong>
                                  {agreement.tieringMethodFriendly || agreement.tieringMethod}
                                </strong>
                              </Text>
                              <Text as="p" variant="small" block className={styles.calculation}>
                                Tiering Reset Interval:{' '}
                                <strong>
                                  {agreement.tieringResetIntervalFriendly ||
                                    agreement.tieringResetInterval}
                                </strong>
                              </Text>
                            </>
                          )}
                          <FilterDetails
                            filters={
                              agreement.tieringMethod
                                ? expandRateAssignments(agreement.rateAssignments).map(r => ({
                                    ...r,
                                    tieringMethod: agreement.tieringMethod,
                                  }))
                                : agreement.rateAssignments
                            }
                            prefix={calculationPrefix(agreement.calculation)}
                          />
                          <FilterDetailsList
                            dimensions={agreement.dimensions}
                            filters={agreement.rateAssignments}
                            calcParams={agreement.calculation?.calcParams}
                            agreementName={agreement.name}
                            calcMethod={agreement.calculation.name}
                            onRenderHeader={getAgreementAndCalcParamHeader(agreement)}
                            onExport={exportFeeToExcel(agreement)}
                            header="Royalty Fee Details"
                            buttonText="View Fee Details"
                          />
                        </SubModuleContent>
                      </SubModule>
                    ),
                )}

              {window.config.enableFallbackAgreement && contract.fallbackAgreement && (
                <SubModule disableAccordian closed>
                  <SubModuleHeader id="royaltyFeeUnassigned" className={styles.noBottomBorder}>
                    Royalty Fee for Unassigned:{' '}
                    {getPrefixSentence(
                      contract.fallbackAgreement.calculation,
                      contract.fallbackAgreement,
                    )}
                  </SubModuleHeader>
                </SubModule>
              )}
            </ModuleContent>
          </Module>

          {/* Advance Groups */}
          {window.config.enableContractAdvanceGroups && (
            <Module>
              {/* Only show edit links if the contract is not pending approval */}
              <ModuleHeader
                extra={
                  !isPendingApproval && (
                    <Protected permissions="manageAdjustments">
                      <Link href={`/contracts/${contractAddress}/add-advancegroup`}>
                        Edit Advance Groups
                      </Link>
                    </Protected>
                  )
                }
              >
                Advance Groups
              </ModuleHeader>
              <ModuleContent>
                {(!advanceGroups || advanceGroups?.length === 0) && (
                  <SubModule closed>
                    <SubModuleHeader id="advanceGroupsHeading">Advance Groups</SubModuleHeader>
                    <SubModuleContent>
                      <MessageBar>
                        <div>
                          <Text as="p" className={styles.messageBarTitle}>
                            No Advance Groups Assigned
                          </Text>
                        </div>
                        <Text as="p" block className={styles.messageBarBody}>
                          No Advance Groups currently assigned to this contract. Select the Edit
                          Advance Groups button above to create a new Advance Group.
                        </Text>
                      </MessageBar>
                    </SubModuleContent>
                  </SubModule>
                )}

                {advanceGroups?.length > 0 && (
                  <DetailsList
                    items={advanceGroups}
                    columns={[{ key: 'name', name: 'advanceGroupName', minWidth: 100 }]}
                    selectionMode={SelectionMode.none}
                    isHeaderVisible={false}
                    onRenderRow={props => (
                      <DetailsRow {...props} className={styles.detailsRowStyle} />
                    )}
                    onRenderItemColumn={(item, index) => (
                      <div className={styles.advanceGroupCard}>
                        <AdvanceGroupCard
                          advanceGroup={item}
                          contractCurrency={contract.contractCurrency}
                          index={index}
                          buttonType={AdvanceGroupCard.BUTTON.VIEWDETAILS}
                        />
                      </div>
                    )}
                  />
                )}
              </ModuleContent>
            </Module>
          )}

          {/* Cancel , Discard Draft and Submit buttons section */}
          {showReviewerSection && (
            <Formik
              initialValues={initialFormValues}
              enableReinitialize
              validateOnChange={false}
              validateOnBlur={false}
              validate={validateFormValues}
              onSubmit={onSubmitForApproval}
            >
              {({ values, submitForm }) => (
                <Form data-testid="contractDetailsReviewerSection">
                  <Module>
                    <ModuleContent>
                      <SubModule disableAccordian>
                        <SubModuleHeader variant="large">Reviewer</SubModuleHeader>
                        <SubModuleContent>
                          <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                            <Field
                              name="reviewerId"
                              label="Reviewer"
                              component={Dropdown}
                              options={reviewers.data || []}
                              required
                              disabled={!isDraft}
                            />

                            <Field
                              name="reviewerJustification"
                              label="Reviewer Justification"
                              component={TextField}
                              disabled={!isDraft}
                              required={
                                values.reviewerId !==
                                get(contract, 'metadata.businessProcessingManager.oid')
                              }
                              multiline
                            />
                          </StackGrid>
                        </SubModuleContent>
                      </SubModule>
                    </ModuleContent>
                  </Module>

                  {isDraft && (
                    <Stack horizontal horizontalAlign="end">
                      {(can('discardContractDraft') || can('submitContractForApproval')) && (
                        <TextButton className={styles.cancelButton} onClick={handleCancel}>
                          Cancel
                        </TextButton>
                      )}
                      {can('discardContractDraft') && (
                        <>
                          <DefaultButton
                            className={styles.discardButton}
                            onClick={() => setIsDiscardHidden(false)}
                            data-testid="discardDraftButton"
                          >
                            Discard Draft
                          </DefaultButton>
                          <DiscardContractDialog
                            onDismiss={() => setIsDiscardHidden(true)}
                            onSubmit={() => handleDiscardDraft()}
                            hidden={isDiscardHidden}
                            isDiscarding={discardDraftApi.isLoading}
                            discardError={discardDraftApi.error}
                            isOnboardedContract={!contract.previousState}
                          />
                        </>
                      )}
                      {can('submitContractForApproval') && (
                        <>
                          <PrimaryButton onClick={submitForm}>
                            Submit for Approval
                            {isContractSubmitting && (
                              <Spinner data-testid="submit-spinner" size={SpinnerSize.medium} />
                            )}
                          </PrimaryButton>
                        </>
                      )}
                    </Stack>
                  )}
                </Form>
              )}
            </Formik>
          )}

          {/* Approve and Reject buttons section */}
          {isPendingApproval && isReviewer && (can('approveContract') || can('rejectContract')) && (
            <ContractApprovalOptions
              contractAddress={contractAddress}
              isEdit={isEditedPendingApproval}
            />
          )}

          {activities && <ContractActivity resourceName={contract?.name} items={activities} />}
        </>
      )}
    </>
  );
};

export default ContractDetails;
